import {
  BookOpen,
  Check,
  ChevronDown,
  FileText,
  Globe2,
  Loader2,
  MoreHorizontal,
  Paperclip,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Collapsible,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { apiRequest } from "../lib/api";
import type {
  AgentRecord,
  KnowledgeBaseDetail,
  KnowledgeBaseRecord,
  KnowledgeDocumentRecord,
  KnowledgeRefreshCadence,
  KnowledgeSourceRecord,
} from "../types";

type KnowledgeBaseSummary = KnowledgeBaseRecord & {
  attachedAgentIds?: string[];
  sourceCount?: number;
  documentCount?: number;
  chunkCount?: number;
};

type KnowledgeBaseListResponse = {
  knowledgeBases: KnowledgeBaseSummary[];
};

type CreateKnowledgeBaseResponse = {
  knowledgeBase: KnowledgeBaseRecord;
};

type KnowledgeBaseDetailResponse = {
  knowledgeBase: KnowledgeBaseDetail;
};

type IngestKnowledgeSourceResponse = {
  sourceId: string;
  jobId?: string;
  status?: string;
  documentCount?: number;
  chunkCount?: number;
  knowledgeBase?: KnowledgeBaseDetail | null;
};

type AgentKnowledgePanelProps = {
  draft: Partial<AgentRecord>;
  disabled?: boolean;
  attachmentDisabled?: boolean;
  attachmentTargetName?: string | null;
  mode?: "manage" | "attach";
  showHeader?: boolean;
  organizationId?: string | null;
  onSaveDraft?: (nextDraft: Partial<AgentRecord>) => Promise<void>;
  onDraftChange?: (nextDraft: Partial<AgentRecord>) => void;
};

type AddMode = "file" | "website";
type WebsiteMode = "page" | "site" | "sitemap";

const cadenceOptions: Array<{ value: KnowledgeRefreshCadence; label: string }> = [
  { value: "manual", label: "Manual" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

const uniqueIds = (ids: string[]): string[] =>
  Array.from(new Set(ids.filter((id) => Boolean(id.trim()))));

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "Never";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const statusClasses = (status: KnowledgeSourceRecord["status"]): string => {
  if (status === "ready") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (status === "processing") {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }

  return "border-red-200 bg-red-50 text-red-700";
};

const sourceIcon = (source: KnowledgeSourceRecord) => {
  if (source.sourceType === "url") {
    return <Globe2 className="h-4 w-4" />;
  }

  if (source.sourceType === "file") {
    return <Paperclip className="h-4 w-4" />;
  }

  return <FileText className="h-4 w-4" />;
};

const sourceDocumentLabel = (source: KnowledgeSourceRecord): string =>
  source.sourceType === "url" ? "pages" : "documents";

const documentUri = (document: KnowledgeDocumentRecord): string | null => {
  const metadata = document.metadata;
  if (!metadata) {
    return null;
  }

  const value = metadata.uri ?? metadata.url ?? metadata.sourceUri;
  return typeof value === "string" && value.trim() ? value : null;
};

export function AgentKnowledgePanel({
  draft,
  disabled,
  attachmentDisabled,
  attachmentTargetName,
  mode = "manage",
  showHeader = true,
  organizationId,
  onSaveDraft,
  onDraftChange,
}: AgentKnowledgePanelProps) {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseSummary[]>([]);
  const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState("");
  const [detail, setDetail] = useState<KnowledgeBaseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [libraryMenuOpen, setLibraryMenuOpen] = useState(false);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [sourceAdvancedOpen, setSourceAdvancedOpen] = useState(false);
  const [expandedSourceIds, setExpandedSourceIds] = useState<string[]>([]);
  const libraryMenuRef = useRef<HTMLDivElement | null>(null);

  const [newKnowledgeBaseName, setNewKnowledgeBaseName] = useState("");
  const [newKnowledgeBaseDescription, setNewKnowledgeBaseDescription] = useState("");
  const [knowledgeBaseName, setKnowledgeBaseName] = useState("");
  const [knowledgeBaseDescription, setKnowledgeBaseDescription] = useState("");
  const [addMode, setAddMode] = useState<AddMode>("file");

  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteMode, setWebsiteMode] = useState<WebsiteMode>("page");
  const [websiteMaxPages, setWebsiteMaxPages] = useState("25");
  const [websiteRenderJs, setWebsiteRenderJs] = useState(false);
  const [websiteCadence, setWebsiteCadence] = useState<KnowledgeRefreshCadence>("weekly");

  const [file, setFile] = useState<File | null>(null);

  const attachedKnowledgeBaseIds = useMemo(
    () => uniqueIds(draft.knowledgeBaseIds ?? []),
    [draft.knowledgeBaseIds],
  );
  const attachedKnowledgeBaseIdSet = useMemo(
    () => new Set(attachedKnowledgeBaseIds),
    [attachedKnowledgeBaseIds],
  );
  const selectedSummary = knowledgeBases.find(
    (knowledgeBase) => knowledgeBase.id === selectedKnowledgeBaseId,
  );
  const selectedIsAttached = selectedKnowledgeBaseId
    ? attachedKnowledgeBaseIdSet.has(selectedKnowledgeBaseId)
    : false;
  const isAttachMode = mode === "attach";
  const canUpdateAttachment =
    isAttachMode &&
    Boolean(draft.id) &&
    !attachmentDisabled &&
    Boolean(onDraftChange) &&
    Boolean(onSaveDraft);

  const setDetailAndList = (nextDetail: KnowledgeBaseDetail | null) => {
    setDetail(nextDetail);
    if (!nextDetail) {
      return;
    }

    setKnowledgeBases((current) =>
      current.map((knowledgeBase) =>
        knowledgeBase.id === nextDetail.id
          ? {
              ...knowledgeBase,
              name: nextDetail.name,
              description: nextDetail.description,
              updatedAt: nextDetail.updatedAt,
              attachedAgentIds: nextDetail.attachedAgentIds,
              sourceCount: nextDetail.sourceCount,
              documentCount: nextDetail.documentCount,
              chunkCount: nextDetail.chunkCount,
            }
          : knowledgeBase,
      ),
    );
  };

  const loadKnowledgeBaseDetail = async (knowledgeBaseId: string) => {
    if (!knowledgeBaseId) {
      setDetail(null);
      return;
    }

    const payload = await apiRequest<KnowledgeBaseDetailResponse>(
      `/api/knowledgebases/${knowledgeBaseId}`,
    );
    setDetailAndList(payload.knowledgeBase);
  };

  const loadKnowledgeBases = async () => {
    if (!organizationId) {
      setKnowledgeBases([]);
      setSelectedKnowledgeBaseId("");
      setDetail(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = await apiRequest<KnowledgeBaseListResponse>("/api/knowledgebases");
      const nextKnowledgeBases = payload.knowledgeBases ?? [];
      setKnowledgeBases(nextKnowledgeBases);

      const preferredId =
        nextKnowledgeBases.find((knowledgeBase) =>
          attachedKnowledgeBaseIdSet.has(knowledgeBase.id),
        )?.id ??
        nextKnowledgeBases.find((knowledgeBase) => knowledgeBase.id === selectedKnowledgeBaseId)
          ?.id ??
        nextKnowledgeBases[0]?.id ??
        "";

      setSelectedKnowledgeBaseId(preferredId);
      if (preferredId) {
        await loadKnowledgeBaseDetail(preferredId);
      } else {
        setDetail(null);
      }
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load knowledgebases",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadKnowledgeBases();
  }, [organizationId]);

  useEffect(() => {
    if (!selectedKnowledgeBaseId) {
      setDetail(null);
      return;
    }

    void loadKnowledgeBaseDetail(selectedKnowledgeBaseId).catch((loadError) => {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Failed to load knowledgebase",
      );
    });
  }, [selectedKnowledgeBaseId]);

  useEffect(() => {
    setKnowledgeBaseName(detail?.name ?? "");
    setKnowledgeBaseDescription(detail?.description ?? "");
  }, [detail?.id, detail?.name, detail?.description]);

  useEffect(() => {
    if (!libraryMenuOpen) {
      return;
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!libraryMenuRef.current?.contains(event.target as Node)) {
        setLibraryMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [libraryMenuOpen]);

  useEffect(() => {
    if (!detail?.sources.some((source) => source.status === "processing")) {
      return;
    }

    const interval = window.setInterval(() => {
      void loadKnowledgeBaseDetail(detail.id).catch(() => {
        // Keep the current table visible if a background refresh poll fails.
      });
    }, 5000);

    return () => window.clearInterval(interval);
  }, [detail?.id, detail?.sources]);

  const saveKnowledgeBaseIds = async (knowledgeBaseIds: string[]) => {
    if (!onDraftChange || !onSaveDraft) {
      throw new Error("Attachment callbacks are required.");
    }

    const nextDraft = {
      ...draft,
      knowledgeBaseIds: uniqueIds(knowledgeBaseIds),
    };

    onDraftChange(nextDraft);
    await onSaveDraft(nextDraft);
  };

  const createKnowledgeBase = async () => {
    const name = newKnowledgeBaseName.trim();

    if (!name) {
      setError("Knowledgebase name is required.");
      return;
    }

    setBusyAction("create-kb");
    setError(null);
    setStatus(null);

    try {
      const payload = await apiRequest<CreateKnowledgeBaseResponse>("/api/knowledgebases", {
        method: "POST",
        body: JSON.stringify({
          name,
          description: newKnowledgeBaseDescription.trim(),
        }),
      });
      setKnowledgeBases((current) => [payload.knowledgeBase, ...current]);
      setSelectedKnowledgeBaseId(payload.knowledgeBase.id);
      setNewKnowledgeBaseName("");
      setNewKnowledgeBaseDescription("");
      setCreateDialogOpen(false);
      if (canUpdateAttachment) {
        await saveKnowledgeBaseIds([...attachedKnowledgeBaseIds, payload.knowledgeBase.id]);
      }
      await loadKnowledgeBaseDetail(payload.knowledgeBase.id);
      setStatus(
        canUpdateAttachment
          ? "Knowledgebase created and attached"
          : "Knowledgebase created",
      );
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Failed to create knowledgebase",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const handleCreateDialogOpenChange = (open: boolean) => {
    setCreateDialogOpen(open);
    if (!open) {
      setNewKnowledgeBaseName("");
      setNewKnowledgeBaseDescription("");
    }
  };

  const setSourceMode = (nextMode: AddMode) => {
    setAddMode(nextMode);
    setSourceAdvancedOpen(false);
  };

  const saveKnowledgeBase = async () => {
    if (!detail) {
      return;
    }

    setBusyAction("save-kb");
    setError(null);
    setStatus(null);

    try {
      const payload = await apiRequest<KnowledgeBaseDetailResponse>(
        `/api/knowledgebases/${detail.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            name: knowledgeBaseName,
            description: knowledgeBaseDescription,
          }),
        },
      );
      setDetailAndList(payload.knowledgeBase);
      setEditDialogOpen(false);
      setStatus("Knowledgebase saved");
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Failed to save knowledgebase",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const toggleSelectedKnowledgeBase = async () => {
    if (!selectedKnowledgeBaseId) {
      return;
    }

    if (!canUpdateAttachment) {
      setError("Select an agent before updating attachment.");
      return;
    }

    setBusyAction("attach-kb");
    setError(null);
    setStatus(null);

    try {
      if (selectedIsAttached) {
        await saveKnowledgeBaseIds(
          attachedKnowledgeBaseIds.filter((id) => id !== selectedKnowledgeBaseId),
        );
        setStatus("Knowledgebase detached");
      } else {
        await saveKnowledgeBaseIds([...attachedKnowledgeBaseIds, selectedKnowledgeBaseId]);
        setStatus("Knowledgebase attached");
      }
    } catch (attachError) {
      setError(
        attachError instanceof Error
          ? attachError.message
          : "Failed to update attachment",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const deleteSelectedKnowledgeBase = async () => {
    if (!detail) {
      return;
    }

    const confirmed = window.confirm(`Delete "${detail.name}" and all indexed sources?`);
    if (!confirmed) {
      return;
    }

    setBusyAction("delete-kb");
    setError(null);
    setStatus(null);

    try {
      await apiRequest<{ success: boolean }>(`/api/knowledgebases/${detail.id}`, {
        method: "DELETE",
      });
      if (canUpdateAttachment) {
        await saveKnowledgeBaseIds(attachedKnowledgeBaseIds.filter((id) => id !== detail.id));
      }
      const remaining = knowledgeBases.filter((knowledgeBase) => knowledgeBase.id !== detail.id);
      setKnowledgeBases(remaining);
      const nextId = remaining[0]?.id ?? "";
      setSelectedKnowledgeBaseId(nextId);
      if (nextId) {
        await loadKnowledgeBaseDetail(nextId);
      } else {
        setDetail(null);
      }
      setStatus("Knowledgebase deleted");
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Failed to delete knowledgebase",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const requireSelectedKnowledgeBase = (): string | null => {
    if (selectedKnowledgeBaseId) {
      return selectedKnowledgeBaseId;
    }

    setError("Create or select a knowledgebase first.");
    return null;
  };

  const applySourceResponse = (
    payload: IngestKnowledgeSourceResponse,
    fallbackStatus: string,
  ) => {
    if (payload.knowledgeBase) {
      setDetailAndList(payload.knowledgeBase);
    }
    setStatus(payload.status === "queued" ? "Source queued for indexing" : fallbackStatus);
  };

  const addWebsiteSource = async () => {
    const knowledgeBaseId = requireSelectedKnowledgeBase();
    if (!knowledgeBaseId) {
      return;
    }

    if (!websiteUrl.trim()) {
      setError("Website URL is required.");
      return;
    }

    setBusyAction("add-website");
    setError(null);
    setStatus(null);

    try {
      const payload = await apiRequest<IngestKnowledgeSourceResponse>(
        `/api/knowledgebases/${knowledgeBaseId}/sources/url`,
        {
          method: "POST",
          body: JSON.stringify({
            url: websiteUrl,
            mode: websiteMode,
            maxPages: Number(websiteMaxPages) || undefined,
            renderJs: websiteRenderJs,
            refreshCadence: websiteCadence,
          }),
        },
      );
      setWebsiteUrl("");
      setSourceDialogOpen(false);
      applySourceResponse(payload, "Source queued for indexing");
    } catch (addError) {
      setError(
        addError instanceof Error ? addError.message : "Failed to add website source",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const addFileSource = async () => {
    const knowledgeBaseId = requireSelectedKnowledgeBase();
    if (!knowledgeBaseId) {
      return;
    }

    if (!file) {
      setError("Choose a file to upload.");
      return;
    }

    setBusyAction("add-file");
    setError(null);
    setStatus(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);
      formData.append("refreshCadence", "manual");
      const payload = await apiRequest<IngestKnowledgeSourceResponse>(
        `/api/knowledgebases/${knowledgeBaseId}/sources/file`,
        {
          method: "POST",
          body: formData,
        },
      );
      setFile(null);
      setSourceDialogOpen(false);
      applySourceResponse(payload, "Source queued for indexing");
    } catch (addError) {
      setError(addError instanceof Error ? addError.message : "Failed to upload file");
    } finally {
      setBusyAction(null);
    }
  };

  const reindexSource = async (source: KnowledgeSourceRecord) => {
    setBusyAction(`reindex-${source.id}`);
    setError(null);
    setStatus(null);

    try {
      const payload = await apiRequest<IngestKnowledgeSourceResponse>(
        `/api/knowledgebases/${source.knowledgeBaseId}/sources/${source.id}/reindex`,
        { method: "POST" },
      );
      applySourceResponse(payload, "Source queued for refresh");
    } catch (reindexError) {
      setError(
        reindexError instanceof Error ? reindexError.message : "Failed to reindex source",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const updateSourceCadence = async (
    source: KnowledgeSourceRecord,
    refreshCadence: KnowledgeRefreshCadence,
  ) => {
    setBusyAction(`schedule-${source.id}`);
    setError(null);
    setStatus(null);

    try {
      const payload = await apiRequest<KnowledgeBaseDetailResponse>(
        `/api/knowledgebases/${source.knowledgeBaseId}/sources/${source.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ refreshCadence }),
        },
      );
      setDetailAndList(payload.knowledgeBase);
      setStatus("Schedule updated");
    } catch (scheduleError) {
      setError(
        scheduleError instanceof Error ? scheduleError.message : "Failed to update schedule",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const deleteSource = async (source: KnowledgeSourceRecord) => {
    const confirmed = window.confirm(`Delete "${source.name}" from this knowledgebase?`);
    if (!confirmed) {
      return;
    }

    setBusyAction(`delete-${source.id}`);
    setError(null);
    setStatus(null);

    try {
      const payload = await apiRequest<KnowledgeBaseDetailResponse>(
        `/api/knowledgebases/${source.knowledgeBaseId}/sources/${source.id}`,
        { method: "DELETE" },
      );
      setDetailAndList(payload.knowledgeBase);
      setStatus("Source deleted");
    } catch (deleteError) {
      setError(
        deleteError instanceof Error ? deleteError.message : "Failed to delete source",
      );
    } finally {
      setBusyAction(null);
    }
  };

  const renderCadenceSelect = (
    value: KnowledgeRefreshCadence,
    onChange: (value: KnowledgeRefreshCadence) => void,
    className = "",
  ) => (
    <Select
      value={value}
      onValueChange={(nextValue) => onChange(nextValue as KnowledgeRefreshCadence)}
    >
      <SelectTrigger className={className}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {cadenceOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const toggleSourcePages = (sourceId: string) => {
    setExpandedSourceIds((current) =>
      current.includes(sourceId)
        ? current.filter((currentId) => currentId !== sourceId)
        : [...current, sourceId],
    );
  };

  const renderAddSourceFields = () => (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <p className="text-sm font-medium text-slate-900">Source type</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {([
            ["file", Upload, "Docs"],
            ["website", Globe2, "Website"],
          ] as const).map(([mode, Icon, label]) => (
            <Button
              key={mode}
              type="button"
              variant={addMode === mode ? "default" : "outline"}
              onClick={() => setSourceMode(mode)}
              className="h-11 justify-start"
            >
              <Icon className="h-4 w-4" />
              {label}
            </Button>
          ))}
        </div>
      </div>

      {addMode === "file" ? (
        <div className="grid gap-3">
          <label className="flex min-h-[150px] cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-slate-300 bg-white px-4 py-5 text-center text-sm text-slate-600 transition hover:border-cyan-300 hover:bg-cyan-50/40">
            <Upload className="mb-2 h-5 w-5 text-slate-500" />
            <span className="font-medium text-slate-800">
              {file ? file.name : "Choose PDF, Markdown, CSV, or JSON"}
            </span>
            <span className="mt-1 text-xs text-slate-500">
              PDFs are extracted through your configured AI provider.
            </span>
            <input
              type="file"
              className="sr-only"
              accept=".pdf,.md,.csv,.json,application/pdf,application/json"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
          <Button
            type="button"
            onClick={() => void addFileSource()}
            disabled={disabled || Boolean(busyAction)}
            className="w-fit"
          >
            {busyAction === "add-file" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload and Index
          </Button>
        </div>
      ) : null}

      {addMode === "website" ? (
        <div className="grid gap-3">
          <Input
            value={websiteUrl}
            onChange={(event) => setWebsiteUrl(event.target.value)}
            placeholder="https://example.com"
          />
          <Collapsible open={sourceAdvancedOpen} onOpenChange={setSourceAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" className="w-fit px-0 text-slate-600">
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    sourceAdvancedOpen ? "rotate-180" : ""
                  }`}
                />
                Advanced
              </Button>
            </CollapsibleTrigger>
            {sourceAdvancedOpen ? (
              <div className="grid gap-4 rounded-md border border-slate-200 bg-slate-50/70 p-4 lg:grid-cols-2">
                <label className="grid gap-1.5 lg:min-h-[132px]">
                  <span className="text-sm font-medium text-slate-800">What to read</span>
                  <Select
                    value={websiteMode}
                    onValueChange={(nextValue) => setWebsiteMode(nextValue as WebsiteMode)}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="page">Only this page</SelectItem>
                      <SelectItem value="site">
                        This page and linked pages on the same site
                      </SelectItem>
                      <SelectItem value="sitemap">Pages listed in a sitemap</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-500">
                    Use "Only this page" unless you want the knowledgebase to follow links.
                  </span>
                </label>

                <label className="grid gap-1.5 lg:min-h-[132px]">
                  <span className="text-sm font-medium text-slate-800">
                    Check for updates
                  </span>
                  {renderCadenceSelect(websiteCadence, setWebsiteCadence, "bg-white")}
                  <span className="text-xs text-slate-500">
                    Manual means it updates only when you refresh it.
                  </span>
                </label>

                <label className="grid gap-1.5">
                  <span className="text-sm font-medium text-slate-800">Page limit</span>
                  <Input
                    value={websiteMaxPages}
                    onChange={(event) => setWebsiteMaxPages(event.target.value)}
                    inputMode="numeric"
                    placeholder="25"
                    className="bg-white"
                  />
                  <span className="text-xs text-slate-500">
                    Applies only when following site links or reading a sitemap.
                  </span>
                </label>

                <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3 text-sm text-slate-700 lg:col-span-2">
                  <input
                    type="checkbox"
                    checked={websiteRenderJs}
                    onChange={(event) => setWebsiteRenderJs(event.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cyan-700 focus:ring-cyan-300"
                  />
                  <span>
                    <span className="block font-medium text-slate-800">
                      Load dynamic content
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">
                      Use this for sites where important text appears after the page loads.
                    </span>
                  </span>
                </label>
              </div>
            ) : null}
          </Collapsible>
          <Button
            type="button"
            onClick={() => void addWebsiteSource()}
            disabled={disabled || Boolean(busyAction)}
            className="w-fit"
          >
            {busyAction === "add-website" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Globe2 className="h-4 w-4" />
            )}
            Index Website
          </Button>
        </div>
      ) : null}
    </div>
  );

  const renderSources = () => {
    const sources = detail?.sources ?? [];

    if (!detail) {
      return (
        <div className="rounded-md border border-dashed border-slate-200 px-5 py-8 text-center text-sm text-slate-500">
          Create a knowledgebase to start adding sources.
        </div>
      );
    }

    if (sources.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-slate-200 px-5 py-8 text-center">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-500">
            <BookOpen className="h-5 w-5" />
          </div>
          <p className="mt-3 text-sm font-medium text-slate-800">No sources indexed</p>
          <p className="mt-1 text-sm text-slate-500">
            Upload docs or crawl a website.
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="border-y border-slate-200 bg-slate-50/70 text-xs uppercase tracking-[0.08em] text-slate-500">
            <TableRow>
              <TableHead className="px-5 font-semibold">Source</TableHead>
              <TableHead className="px-3 font-semibold">Status</TableHead>
              <TableHead className="px-3 font-semibold">Indexed</TableHead>
              <TableHead className="px-3 font-semibold">Updated</TableHead>
              <TableHead className="px-3 font-semibold">Schedule</TableHead>
              <TableHead className="px-3 font-semibold">Next</TableHead>
              <TableHead className="px-5 text-right font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sources.map((source) => {
              const documents = source.documents ?? [];
              const pagesExpanded = expandedSourceIds.includes(source.id);

              return (
                <Fragment key={source.id}>
                  <TableRow className="align-top hover:bg-transparent">
                    <TableCell className="max-w-[360px] px-5 py-4">
                      <div className="flex min-w-0 items-start gap-3">
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500">
                          {sourceIcon(source)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">{source.name}</p>
                          <p className="mt-1 truncate text-xs text-slate-500">
                            {source.uri || source.sourceType}
                          </p>
                          {source.lastIndexError ? (
                            <p className="mt-2 rounded border border-red-100 bg-red-50 px-2 py-1 text-xs text-red-700">
                              {source.lastIndexError}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-3 py-4">
                      <Badge className={statusClasses(source.status)} variant="outline">
                        {source.status}
                      </Badge>
                      {source.status !== "ready" && source.statusMessage ? (
                        <p className="mt-1 text-xs text-slate-500">{source.statusMessage}</p>
                      ) : null}
                    </TableCell>
                    <TableCell className="px-3 py-4">
                      <p className="text-slate-700">{formatDate(source.lastIndexedAt)}</p>
                      {documents.length > 0 ? (
                        <button
                          type="button"
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
                          aria-expanded={pagesExpanded}
                          onClick={() => toggleSourcePages(source.id)}
                        >
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform ${
                              pagesExpanded ? "rotate-180" : ""
                            }`}
                          />
                          {source.documentCount ?? 0} {sourceDocumentLabel(source)}
                        </button>
                      ) : (
                        <p className="mt-1 text-xs text-slate-500">
                          0 {sourceDocumentLabel(source)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-4 text-slate-700">
                      {formatDate(source.updatedAt)}
                    </TableCell>
                    <TableCell className="px-3 py-4">
                      {renderCadenceSelect(
                        source.refreshCadence,
                        (value) => void updateSourceCadence(source, value),
                        "w-[120px]",
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-4 text-slate-700">
                      {formatDate(source.nextIndexAt)}
                    </TableCell>
                    <TableCell className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          title="Reindex source"
                          onClick={() => void reindexSource(source)}
                          disabled={disabled || Boolean(busyAction)}
                        >
                          {busyAction === `reindex-${source.id}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          title="Delete source"
                          onClick={() => void deleteSource(source)}
                          disabled={disabled || Boolean(busyAction)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          {busyAction === `delete-${source.id}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {documents.length > 0 && pagesExpanded ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={7} className="px-5 pb-4 pt-0">
                        <div className="ml-11 grid gap-2 border-t border-slate-100 pt-3">
                          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Pages
                          </div>
                          <div className="divide-y divide-slate-100">
                            {documents.map((document) => {
                              const uri = documentUri(document);

                              return (
                                <div
                                  key={document.id}
                                  className="grid gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                                >
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-slate-900">
                                      {document.title}
                                    </p>
                                    {uri ? (
                                      <p className="mt-1 truncate text-xs text-slate-500">
                                        {uri}
                                      </p>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-slate-500 sm:text-right">
                                    Updated {formatDate(document.updatedAt)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderAttachmentDetail = () => {
    if (!detail) {
      return (
        <div className="border-b border-slate-200 px-5 py-4 text-sm text-slate-500">
          Select a knowledgebase to attach.
        </div>
      );
    }

    return (
      <>
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-slate-500">
                Selected Library
              </p>
              <h4 className="mt-1 truncate text-base font-semibold text-slate-900">
                {detail.name}
              </h4>
              {detail.description ? (
                <p className="mt-1 text-sm text-slate-500">{detail.description}</p>
              ) : null}
              <p className="mt-2 text-xs text-slate-500">
                {detail.sourceCount} sources, {detail.documentCount} documents indexed
              </p>
            </div>
            <Button
              type="button"
              variant={selectedIsAttached ? "secondary" : "outline"}
              onClick={() => void toggleSelectedKnowledgeBase()}
              disabled={disabled || Boolean(busyAction) || !canUpdateAttachment}
            >
              {selectedIsAttached ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {selectedIsAttached ? "Detach" : "Attach"}
            </Button>
          </div>
        </div>
        <div className="px-5 py-10 text-center text-sm text-slate-500">
          Source management is in Knowledgebase.
        </div>
      </>
    );
  };

  const renderKnowledgeBaseSelector = () => (
    <div className="border-b border-slate-200 bg-slate-50/70 px-5 py-4">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
        <label className="block">
          <span className="mb-1 flex items-center justify-between gap-3 text-xs font-medium uppercase tracking-[0.08em] text-slate-500">
            <span>Library</span>
            <span className="tracking-normal text-slate-400">
              {knowledgeBases.length} libraries
            </span>
          </span>
          <Select
            value={selectedKnowledgeBaseId}
            onValueChange={setSelectedKnowledgeBaseId}
            disabled={loading || knowledgeBases.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="No knowledgebases yet" />
            </SelectTrigger>
            <SelectContent>
              {knowledgeBases.map((knowledgeBase) => {
                const attached = attachedKnowledgeBaseIdSet.has(knowledgeBase.id);
                const attachmentLabel = isAttachMode && attached ? " - attached" : "";

                return (
                  <SelectItem key={knowledgeBase.id} value={knowledgeBase.id}>
                    {knowledgeBase.name} - {knowledgeBase.sourceCount ?? 0} sources,
                    {" "}
                    {knowledgeBase.documentCount ?? 0} documents{attachmentLabel}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </label>
        <div className="flex items-center gap-2">
          {!isAttachMode ? (
            <Button
              type="button"
              onClick={() => handleCreateDialogOpenChange(true)}
              disabled={disabled || Boolean(busyAction)}
            >
              <Plus className="h-4 w-4" />
              New
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => void loadKnowledgeBases()}
            disabled={loading}
            title="Refresh knowledgebases"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      {showHeader ? (
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-cyan-50 text-cyan-700">
                <BookOpen className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-slate-900">
                  {isAttachMode ? "Agent Knowledgebases" : "Knowledgebase"}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {isAttachMode
                    ? `Attach existing knowledgebases to ${
                        attachmentTargetName ?? "this agent"
                      }.`
                    : "Manage indexed sources and refresh schedules."}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mx-6 mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {status ? (
        <div className="mx-6 mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {status}
        </div>
      ) : null}

      <div className="min-h-[560px]">
        {renderKnowledgeBaseSelector()}
        <div className="min-w-0">
          {isAttachMode ? (
            renderAttachmentDetail()
          ) : (
            <>
              <div className="border-b border-slate-200 px-5 py-4">
                {detail ? (
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <h4 className="truncate text-sm font-semibold text-slate-900">
                        {detail.name}
                      </h4>
                      <p className="mt-1 text-sm text-slate-500">
                        {detail.description || "No description"}
                      </p>
                      <p className="mt-2 text-xs text-slate-500">
                        {detail.sourceCount} sources, {detail.documentCount} documents indexed
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        onClick={() => setSourceDialogOpen(true)}
                        disabled={disabled || Boolean(busyAction)}
                      >
                        <Upload className="h-4 w-4" />
                        Add source
                      </Button>
                      <div ref={libraryMenuRef} className="relative">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setLibraryMenuOpen((open) => !open)}
                          disabled={disabled || Boolean(busyAction)}
                          title="More actions"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                        {libraryMenuOpen ? (
                          <div className="absolute right-0 z-20 mt-2 w-44 rounded-md border border-slate-200 bg-white p-1 text-sm shadow-lg">
                            <button
                              type="button"
                              onClick={() => {
                                setLibraryMenuOpen(false);
                                setEditDialogOpen(true);
                              }}
                              className="flex w-full items-center rounded px-3 py-2 text-left text-slate-700 hover:bg-slate-50"
                            >
                              Edit details
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setLibraryMenuOpen(false);
                                void deleteSelectedKnowledgeBase();
                              }}
                              className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">
                    Select or create a knowledgebase.
                  </div>
                )}
              </div>

              <div className="p-5">
                {renderSources()}
              </div>
            </>
          )}
        </div>
      </div>
      <Dialog open={createDialogOpen} onOpenChange={handleCreateDialogOpenChange}>
        <DialogContent className="max-w-md bg-white">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void createKnowledgeBase();
            }}
            className="grid gap-5"
          >
            <DialogHeader>
              <DialogTitle>New knowledgebase</DialogTitle>
              <DialogDescription>
                Create a library, then add docs or website sources.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4">
              <label className="grid gap-2 text-sm text-slate-700">
                Name
                <Input
                  autoFocus
                  value={newKnowledgeBaseName}
                  onChange={(event) => setNewKnowledgeBaseName(event.target.value)}
                  placeholder="Customer support docs"
                  required
                />
              </label>
              <label className="grid gap-2 text-sm text-slate-700">
                Description
                <Input
                  value={newKnowledgeBaseDescription}
                  onChange={(event) => setNewKnowledgeBaseDescription(event.target.value)}
                  placeholder="Optional"
                />
              </label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleCreateDialogOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={disabled || busyAction === "create-kb"}
              >
                {busyAction === "create-kb" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Manage knowledgebase</DialogTitle>
            <DialogDescription>
              Update library details or delete this knowledgebase.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <label className="grid gap-2 text-sm text-slate-700">
              Name
              <Input
                value={knowledgeBaseName}
                onChange={(event) => setKnowledgeBaseName(event.target.value)}
              />
            </label>
            <label className="grid gap-2 text-sm text-slate-700">
              Description
              <Input
                value={knowledgeBaseDescription}
                onChange={(event) => setKnowledgeBaseDescription(event.target.value)}
                placeholder="Optional"
              />
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void saveKnowledgeBase()}
              disabled={disabled || Boolean(busyAction)}
            >
              {busyAction === "save-kb" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={sourceDialogOpen} onOpenChange={setSourceDialogOpen}>
        <DialogContent
          className={`bg-white ${
            sourceAdvancedOpen && addMode === "website" ? "max-w-3xl" : "max-w-lg"
          }`}
        >
          <DialogHeader>
            <DialogTitle>Add source</DialogTitle>
            <DialogDescription>
              Upload docs or crawl a website for the selected knowledgebase.
            </DialogDescription>
          </DialogHeader>
          {renderAddSourceFields()}
        </DialogContent>
      </Dialog>
    </section>
  );
}
